import { colors } from '@packages/core/styles';
import { getExtension } from '@packages/core/utils';
import { AssetType, type TechlineAssetEntity } from '@packages/domain/techline/assets';
import { Icon, LoadingCircle, ProgressBar, Typography } from '@packages/ui/shared';
import { isRenderableImage } from '@web/utils/is-renderable-image';
import { format } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';

import SepfFileImage from '../../../../assets/sepf-file.svg';
import { AttachmentPreview } from './attachment-preview';

/** Props for the Attachment component. */
export type AttachmentProps = {
    /** The asset data to render. */
    asset: TechlineAssetEntity;

    /** Whether the attachment is in read-only mode. Hides edit and delete actions. */
    isReadOnly?: boolean;

    /** Callback when the user clicks delete. */
    onDelete: (assetId: string) => void;

    /** Callback when the user clicks download. */
    onDownload: (assetId: string) => void;

    /** Callback when the user clicks edit (only for complete images). */
    onEdit: (assetId: string) => void;

    /** Callback when the user clicks view (only for complete images). */
    onView: (assetId: string) => void;
};

/** Static file-type icons keyed by lowercase extension. */
const FILE_TYPE_IMAGES: Record<string, string> = {
    sepf: SepfFileImage,
};

/**
 * Render a single attachment item. Purely presentational; displays different
 * UI based on the asset's lifecycle status (uploading, processing, complete, error).
 *
 * @param props - the attachment data and callbacks
 * @returns the rendered attachment item
 */
export function Attachment({ asset, isReadOnly = false, onDelete, onDownload, onEdit, onView }: AttachmentProps) {
    const classes = useStyles();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState<'top' | 'bottom'>('bottom');
    const menuRef = useRef<HTMLDivElement | null>(null);

    // Derived state
    const fileExtension = getExtension(asset.name);
    const isImage = isImageContentType(asset);
    const fileTypeIcon = FILE_TYPE_IMAGES[fileExtension];

    const thumbnailUrl = findThumbnailUrl(asset);
    const showEditButton = !isReadOnly && asset.assetType === AssetType.Image;

    // Handlers

    /** Toggle the context menu, calculating whether to show above or below. */
    const handleMenuButtonClick = useCallback(() => {
        if (isMenuOpen) {
            setIsMenuOpen(false);
            return;
        }

        // Calculate available space below the button
        if (menuRef.current) {
            const rect = menuRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            setMenuPosition(spaceBelow < 280 ? 'top' : 'bottom');
        }
        setIsMenuOpen(true);
    }, [isMenuOpen]);

    /** Handle delete click; closes menu first. */
    const handleDeleteButtonClick = useCallback(() => {
        setIsMenuOpen(false);
        onDelete(asset.id);
    }, [asset.id, onDelete]);

    /** Handle download click; closes menu first. */
    const handleDownloadButtonClick = useCallback(() => {
        setIsMenuOpen(false);
        onDownload(asset.id);
    }, [asset.id, onDownload]);

    /** Handle edit click; closes menu first. */
    const handleEditButtonClick = useCallback(() => {
        setIsMenuOpen(false);
        onEdit(asset.id);
    }, [asset.id, onEdit]);

    /** Handle view click; closes menu first. */
    const handleViewButtonClick = useCallback(() => {
        setIsMenuOpen(false);
        onView(asset.id);
    }, [asset.id, onView]);

    /** Cancel an in-progress upload by deleting the asset. */
    const handleCancelUpload = useCallback(() => {
        onDelete(asset.id);
    }, [asset.id, onDelete]);

    // Effects

    /** Close menu when clicking outside. */
    useEffect(() => {
        /**
         * Close the context menu when the user clicks outside the menu container.
         *
         * @param event - the mouse event
         */
        function handleClickOutside(event: MouseEvent) {
            if (isMenuOpen && menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsMenuOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isMenuOpen]);

    // Determine menu position class
    const menuPositionClass = menuPosition === 'top' ? classes.menuStyleTop : classes.menuStyleBottom;

    return (
        <div className={classes.attachmentItem}>
            <div className={classes.fileInfoBlock}>
                <AttachmentPreview
                    fileTypeIcon={fileTypeIcon}
                    isImage={isImage}
                    isProcessing={asset.status === 'uploading' || asset.status === 'processing'}
                    name={asset.name}
                    onView={handleViewButtonClick}
                    thumbnailUrl={thumbnailUrl}
                />
                <div className={classes.fileInfo}>
                    <span className={classes.fileName}>{asset.name}</span>
                    {asset.status === 'uploading' && (
                        <ProgressBar onCancel={handleCancelUpload} percent={asset.progress} />
                    )}
                    {asset.status === 'processing' && (
                        <div className={classes.processingContainer}>
                            <LoadingCircle size={14} />
                            <span className={classes.processingText}>Processing...</span>
                        </div>
                    )}
                    {asset.status === 'error' && (
                        <span className={classes.errorText}>{asset.error ?? 'Upload failed'}</span>
                    )}
                    {asset.status === 'complete' && (
                        <Typography color="textDarkSecondary" variant="caption">
                            {format(new Date(asset.createdAt), 'PPpp')}
                        </Typography>
                    )}
                </div>
            </div>

            {asset.status === 'complete' && (
                <div className={classes.uploadingContainer} ref={menuRef}>
                    {asset.isWorksheetAsset && (
                        <div className={classes.worksheetBadge}>
                            <Icon color="blueOne" name="paperclip" size={14} />
                            WORKSHEET ASSET
                        </div>
                    )}
                    <div className={classes.uploadingTextContainer}>
                        <Icon color="greenOne" name="check-circle-hollow" size={14} />
                        UPLOADED
                    </div>
                    <Icon
                        color="blueOne"
                        data-testid={`more-menu-${asset.id}`}
                        name="more-dots-vertical"
                        onPress={handleMenuButtonClick}
                        size={18}
                    />
                    {isMenuOpen && (
                        <div className={`${classes.menuStyle} ${menuPositionClass}`}>
                            <div className={classes.menuContainer}>
                                <div className={classes.menuBtn} onClick={handleViewButtonClick}>
                                    <Icon color="grayFive" name="eye" size={24} />
                                    View
                                </div>

                                {showEditButton && (
                                    <div
                                        className={asset.isWorksheetAsset ? classes.menuBtnDisabled : classes.menuBtn}
                                        onClick={asset.isWorksheetAsset ? undefined : handleEditButtonClick}
                                    >
                                        <Icon color="grayFive" name="pencil" size={24} />
                                        Edit
                                    </div>
                                )}
                                <div className={classes.menuBtn} onClick={handleDownloadButtonClick}>
                                    <Icon color="grayFive" name="download" size={24} />
                                    Download
                                </div>
                                {!isReadOnly && (
                                    <div
                                        className={asset.isWorksheetAsset ? classes.menuBtnDisabled : classes.menuBtn}
                                        onClick={asset.isWorksheetAsset ? undefined : handleDeleteButtonClick}
                                        style={asset.isWorksheetAsset ? undefined : deleteMenuItemStyle}
                                    >
                                        <Icon
                                            color={asset.isWorksheetAsset ? 'grayFive' : 'redOne'}
                                            name="trash-striped"
                                            size={24}
                                        />
                                        Delete
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * Find the thumbnail URL from an asset's media items.
 *
 * @param asset - the asset to search
 * @returns the thumbnail URL, or undefined if not found
 */
function findThumbnailUrl(asset: TechlineAssetEntity): string | undefined {
    if (asset?.thumbnailUrl) {
        return asset.thumbnailUrl;
    }

    const isImage = isImageContentType(asset);

    if (isImage) {
        return asset.downloadUrl;
    }
}

/**
 *  Check if the asset's content type or asset type indicates it's an image,
 *  accounting for cases where content type may be missing.
 *
 * @param asset - the asset to evaluate
 * @returns - true if the asset is an image type, false otherwise
 */
function isImageContentType(asset: TechlineAssetEntity): boolean {
    if (asset.assetType === AssetType.Image) {
        return true;
    }

    return isRenderableImage(asset.contentType);
}

// Style constants

const deleteMenuItemStyle = { color: colors.redOne };

const useStyles = createUseStyles({
    attachmentItem: {
        alignItems: 'center',
        backgroundColor: colors.white,
        border: `1px solid ${colors.grayFive}`,
        borderRadius: 8,
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 12,
        padding: '12px 16px',
    },
    errorText: {
        color: colors.redOne,
        fontSize: 12,
    },
    fileInfo: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        gap: 8,
    },
    fileInfoBlock: {
        alignItems: 'center',
        display: 'flex',
        flex: 1,
        gap: 12,
    },
    fileName: {
        color: colors.textDarkPrimary,
        fontSize: 15,
        fontWeight: 600,
    },
    menuBtn: {
        alignItems: 'center',
        borderRadius: 4,
        color: colors.textDarkPrimary,
        cursor: 'pointer',
        display: 'flex',
        fontSize: 15,
        gap: 8,
        padding: '12px',
        transition: 'background-color 0.2s ease',
        '&:hover': {
            backgroundColor: colors.grayOne,
        },
    },
    menuBtnDisabled: {
        alignItems: 'center',
        borderRadius: 4,
        color: colors.grayFive,
        cursor: 'default',
        display: 'flex',
        fontSize: 15,
        gap: 8,
        opacity: 0.5,
        padding: '12px',
    },
    menuContainer: {
        padding: '12px 10px',
    },
    menuStyle: {
        backgroundColor: colors.white,
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 6,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        minWidth: 256,
        position: 'absolute',
        right: 0,
        zIndex: 10,
    },
    menuStyleBottom: {
        top: 32,
    },
    menuStyleTop: {
        bottom: 32,
    },
    processingContainer: {
        alignItems: 'center',
        display: 'flex',
        gap: 6,
    },
    processingText: {
        color: colors.grayFive,
        fontSize: 12,
        fontStyle: 'italic',
    },
    uploadingContainer: {
        alignItems: 'center',
        display: 'flex',
        gap: 12,
        position: 'relative',
    },
    uploadingTextContainer: {
        alignItems: 'center',
        backgroundColor: '#DEEDE8',
        borderRadius: 4,
        display: 'flex',
        fontSize: 12,
        fontWeight: 500,
        gap: 4,
        lineHeight: '16px',
        padding: '0px 8px',
    },
    worksheetBadge: {
        alignItems: 'center',
        backgroundColor: '#DEEAF6',
        borderRadius: 4,
        color: colors.blueOne,
        display: 'flex',
        fontSize: 12,
        fontWeight: 500,
        gap: 4,
        lineHeight: '16px',
        padding: '0px 8px',
    },
});
